import OperationalScopeModal from '../../components/Users/OperationalScopeModal';
import LumaResourceWorkspace, {lumaMoney} from '../../components/admin/LumaResourceWorkspace';
// UsersPage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../components/Loading';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Trash2, Edit, Plus, ChevronRight, Home, ShieldCheck } from 'lucide-react';
import UserModal from '../../components/Users/UserModal';
import EditUserModal from '../../components/Users/EditUserModal';
import DeleteUserModal from '../../components/Users/DeleteUserModal';
import Pagination from '../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../components/Expired/ExpiredSessionModal';
import UserAvatar from '../../components/Users/UserAvatar';
import { getUsers } from '../../services/userService';
import { fetchClientes } from '../../services/clienteService';
import { fetchPersonal } from '../../services/personalService';
import { useSessionExpiration } from '../../hooks/useSessionExpiration';
import { useAuth } from '../../context/AuthContext';
import { useModelPermission } from '../../hooks/useModelPermission';

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `

.usr-page { font-family: 'DM Sans', sans-serif; }
@keyframes usrFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.usr-fadeup   { animation:usrFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.usr-fadeup-1 { animation:usrFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.usr-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.usr-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32);}
.usr-table-row{transition:background .15s;}
.usr-table-row:hover{background:var(--ad-soft, #fafcff);}
.usr-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const fmtDate = (d: string) => {
    if (!d) return 'Nunca';
    try { return new Date(d).toLocaleString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}); } catch { return d; }
};

const UsersPage: React.FC = () => {
    const [isModalOpen, setIsModalOpen]           = useState(false);
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const [isActive, setIsActive]                 = useState(true);
    const [users, setUsers]                       = useState<any[]>([]);
    const [fotoMap, setFotoMap]                   = useState<Record<number, string|null>>({});
    const [currentPage, setCurrentPage]           = useState(1);
    const itemsPerPage = 10;
    const [scopeUser,setScopeUser]=useState<any>(null);
    const [selectedUser, setSelectedUser]         = useState<any>(null);
    const [isEditModalOpen, setIsEditModalOpen]   = useState(false);
    const [selectedUserForDelete, setSelectedUserForDelete] = useState<any>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const { user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('customuser', userLevelId);

    const loadUsers = useCallback(async () => {
        try {
            if (perm?.can_read) setUsers(await getUsers());
            else setUsers([]);
        } catch { setUsers([]); }
    }, [perm]);

    const loadFotos = useCallback(async () => {
        try {
            const [clientes, personal] = await Promise.all([fetchClientes(), fetchPersonal()]);
            const map: Record<number,string|null> = {};
            clientes.forEach((c: any) => { if (c.usuario?.id != null) map[c.usuario.id] = c.foto_url ?? null; });
            personal.forEach((p: any) => { if (p.usuario?.id != null && !(p.usuario.id in map)) map[p.usuario.id] = p.foto_url ?? null; });
            setFotoMap(map);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { loadUsers(); loadFotos(); }, [loadUsers, loadFotos]);

    const sortedUsers = useMemo(() => [...users].sort((a,b)=>a.id-b.id), [users]);
    const totalPages  = Math.ceil(sortedUsers.length / itemsPerPage);
    const paginatedUsers = sortedUsers.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    const handleUserCreated  = (u: any) => setUsers(p=>[...p,u]);
    const handleUserUpdated  = (u: any) => setUsers(p=>p.map(x=>x.id===u.id?{...x,...u}:x));
    const handleUserDeleted  = (id: number) => setUsers(p=>p.filter(x=>x.id!==id));

    if (perm === null) return <Loading text="Cargando usuarios..." />;

    return <><LumaResourceWorkspace title="Usuarios" description="Consulta y gestiona la información de tu coworking." group="Usuarios" rows={users.map(r=>({...r,nombre_completo:`${r.first_name} ${r.last_name}`}))} columns={[{key:'nombre_completo',label:'Nombre'},{key:'email',label:'Email'},{key:'tipo_nivel_usuario',label:'Nivel de usuario',render:r=>r.tipo_nivel_usuario?.nombre||'Sin nivel'},{key:'estado',label:'Estado'}]} idKey="id" nameKey="nombre_completo" statusKey="estado" extraActions={(user?.es_administrador||['Administrador','Superadministrador','Superadmin'].includes(user?.role||''))&&perm.can_update?r=>r.tipo_nivel_usuario?.perfil_asociado==='personal'?<button type="button" onClick={()=>setScopeUser(r)}>Permisos y alcance</button>:null:undefined} canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedUser(r);setIsEditModalOpen(true);}:undefined} onDelete={perm?.can_delete?r=>{setSelectedUserForDelete(r);setIsDeleteModalOpen(true);}:undefined}/>{scopeUser&&<OperationalScopeModal key={scopeUser.id} user={scopeUser} onClose={()=>setScopeUser(null)}/>}<ExpiredSessionModal isOpen={isSessionExpired} onClose={()=>{ setIsSessionExpired(false); window.location.href='/login'; }}/><UserModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} isActive={isActive} toggleActive={()=>setIsActive(!isActive)} onUserCreated={handleUserCreated}/>{isEditModalOpen && selectedUser && <EditUserModal user={selectedUser} onClose={()=>{ setIsEditModalOpen(false); setSelectedUser(null); }} onUserUpdated={handleUserUpdated}/>}{isDeleteModalOpen && selectedUserForDelete && <DeleteUserModal user={selectedUserForDelete} onClose={()=>{ setIsDeleteModalOpen(false); setSelectedUserForDelete(null); }} onUserDeleted={handleUserDeleted}/>}</>;
};

export default UsersPage;
